import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Card,
    CardContent,
    CardMedia
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGetCampaigns, useCreatePitch } from '../../react-query/outreach-queries';
import { toast } from 'react-hot-toast';
import { Podcast } from '../../types/podcast';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';

interface PodcastAddToCampaignModalProps {
    open: boolean;
    onClose: () => void;
    podcast: Podcast;
}

export default function PodcastAddToCampaignModal({ 
    open, 
    onClose, 
    podcast
}: PodcastAddToCampaignModalProps) {
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
    const { data: campaignsData, isLoading: campaignsLoading } = useGetCampaigns();
    const campaigns = campaignsData || [];
    const createPitchMutation = useCreatePitch();

    const handleSubmit = () => {
        if (!selectedCampaignId) return;

        createPitchMutation.mutate(
            {
                campaignId: selectedCampaignId,
                podcastId: podcast.id.toString(),
                notes: `Added from Podcast Details`
            },
            {
                onSuccess: () => {
                    toast.success("Podcast added to campaign successfully");
                    onClose();
                    setSelectedCampaignId('');
                },
                onError: (error) => {
                    console.error(error);
                    toast.error("Failed to add podcast to campaign");
                }
            }
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div">
                    Add to Campaign
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box mb={3}>
                     <Card sx={{ display: 'flex', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                        <CardMedia
                            component="img"
                            sx={{ width: 100, objectFit: 'cover' }}
                            image={podcast.imageUrl || "https://via.placeholder.com/100"}
                            alt={podcast.title}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <CardContent sx={{ flex: '1 0 auto', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                                <Typography component="div" variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                                    {podcast.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    by {podcast.author}
                                </Typography>
                                
                                <Box display="flex" gap={2} mt={1}>
                                    {podcast.estimatedListeners != null && podcast.estimatedListeners > 0 && (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {podcast.estimatedListeners.toLocaleString()} listeners
                                            </Typography>
                                        </Box>
                                    )}
                                    {podcast.appleRating && (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <StarIcon sx={{ fontSize: 14, color: 'orange' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {podcast.appleRating.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Box>
                    </Card>
                </Box>
                
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select a campaign to add this podcast to:
                    </Typography>
                </Box>

                {campaignsLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel id="campaign-select-label">Select Campaign</InputLabel>
                        <Select
                            labelId="campaign-select-label"
                            id="campaign-select"
                            value={selectedCampaignId}
                            label="Select Campaign"
                            onChange={(e) => setSelectedCampaignId(e.target.value)}
                        >
                            {campaigns.length === 0 ? (
                                <MenuItem disabled value="">
                                    No campaigns found
                                </MenuItem>
                            ) : (
                                campaigns.map((campaign: any) => (
                                    <MenuItem key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={!selectedCampaignId || createPitchMutation.isPending}
                    startIcon={createPitchMutation.isPending ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {createPitchMutation.isPending ? 'Adding...' : 'Add to Campaign'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
